import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const LEVELS = [
  '6ème',
  '5ème',
  '4ème',
  '3ème',
  'Seconde',
  'Première',
  'Terminale',
];

const ACADEMIC_YEARS = ['2025-2026', '2026-2027', '2027-2028'];

const AdminClasses = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    room: '',
    max_students: 40,
    class_teacher_id: '',
    academic_year: '2026-2027',
    is_active: true,
  });

  // ============ TOAST ============
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // ============ CHARGEMENT ============
  useEffect(() => {
    fetchClasses();
    fetchTeachers();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/classes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/teachers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeachers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur profs:', error);
    }
  };

  // ============ STATS ============
  const stats = useMemo(() => {
    const totalClasses = classes.length;
    const totalStudents = classes.reduce(
      (sum, c) => sum + (parseInt(c.students_count) || 0),
      0
    );
    const totalCapacity = classes.reduce(
      (sum, c) => sum + (parseInt(c.max_students) || 0),
      0
    );
    const freePlaces = Math.max(0, totalCapacity - totalStudents);
    const withTeacher = classes.filter(
      (c) => c.class_teacher_id || c.class_teacher || c.classTeacher
    ).length;

    return { totalClasses, totalStudents, totalCapacity, freePlaces, withTeacher };
  }, [classes]);

  const levels = useMemo(() => {
    const set = new Set(classes.map((c) => c.level).filter(Boolean));
    return Array.from(set).sort();
  }, [classes]);

  const filteredClasses = useMemo(() => {
    return classes.filter((c) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!(c.name || '').toLowerCase().includes(term)) return false;
      }
      if (filterLevel !== 'all' && c.level !== filterLevel) return false;
      return true;
    });
  }, [classes, searchTerm, filterLevel]);

  // ============ CRUD ============
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        level: formData.level,
        room: formData.room || null,
        max_students: formData.max_students || 40,
        class_teacher_id: formData.class_teacher_id || null,
        academic_year: formData.academic_year,
        is_active: formData.is_active,
      };

      if (editingClass) {
        await api.put(`/classes/${editingClass.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast(`Classe modifiée avec succès !`, 'success');
      } else {
        const res = await api.post('/classes', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showToast(
          res.data?.message || `Classe ${formData.level} ${formData.name} créée !`,
          'success'
        );
      }
      setShowModal(false);
      setEditingClass(null);
      resetForm();
      fetchClasses();
    } catch (error) {
      console.error('Erreur:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Erreur lors de l'enregistrement";
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette classe ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/classes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Classe supprimée avec succès !', 'success');
      fetchClasses();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(
        error.response?.data?.message || 'Erreur lors de la suppression',
        'error'
      );
    }
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setFormData({
      name: classItem.section || classItem.name?.replace(classItem.level, '').trim() || '',
      level: classItem.level || '',
      room: classItem.room || '',
      max_students: classItem.max_students || 40,
      class_teacher_id: classItem.class_teacher_id || '',
      academic_year: classItem.academic_year || '2026-2027',
      is_active: classItem.is_active !== false,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      level: '',
      room: '',
      max_students: 40,
      class_teacher_id: '',
      academic_year: '2026-2027',
      is_active: true,
    });
  };

  const handleNew = () => {
    setEditingClass(null);
    resetForm();
    setShowModal(true);
  };

  const getEffectifColor = (count, max) => {
    const ratio = max > 0 ? count / max : 0;
    if (ratio >= 0.9) return 'bg-red-500';
    if (ratio >= 0.7) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Nom complet affiché : "6ème A"
  const getFullName = (c) => {
    if (c.name && c.level && c.name.startsWith(c.level)) return c.name;
    return `${c.level || ''} ${c.section || c.name || ''}`.trim();
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="text-xl">
            {toast.type === 'success' ? '✅' : '❌'}
          </span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">
            🏫 Gestion des classes
          </h1>
          <p className="text-gray-500">
            Créez et gérez les classes de votre établissement
          </p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
        >
          ➕ Nouvelle classe
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">🏫 Classes</p>
          <p className="text-2xl font-bold text-[#2C3E50]">{stats.totalClasses}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">👨‍🎓 Élèves</p>
          <p className="text-2xl font-bold text-green-600">{stats.totalStudents}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 mb-1">📦 Places libres</p>
          <p className="text-2xl font-bold text-orange-600">{stats.freePlaces}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
          <p className="text-xs text-gray-500 mb-1">👨‍🏫 Profs principaux</p>
          <p className="text-2xl font-bold text-purple-600">
            {stats.withTeacher}/{stats.totalClasses}
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher une classe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Tous les niveaux</option>
            {levels.map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </select>
          <div className="text-sm text-gray-500 flex items-center px-2">
            {filteredClasses.length} classe(s) affichée(s) sur {classes.length}
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-md p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">
            {classes.length === 0 ? 'Aucune classe' : 'Aucun résultat'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {classes.length === 0
              ? 'Commencez par créer votre première classe.'
              : 'Essayez un autre filtre.'}
          </p>
          {classes.length === 0 && (
            <button
              onClick={handleNew}
              className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition"
            >
              ➕ Créer une classe
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClasses.map((classItem) => {
            const count = parseInt(classItem.students_count) || 0;
            const max = parseInt(classItem.max_students) || 40;
            const ratio = max > 0 ? Math.min(100, Math.round((count / max) * 100)) : 0;
            const teacherName =
              classItem.class_teacher?.name ||
              classItem.classTeacher?.name ||
              classItem.class_teacher_name ||
              'Non assigné';
            const hasNoTeacher = teacherName === 'Non assigné';

            return (
              <div
                key={classItem.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden border-t-4 border-[#E67E22]"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-[#2C3E50]">
                        {getFullName(classItem)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {classItem.academic_year || '2024-2025'}
                        {classItem.room ? ` • ${classItem.room}` : ''}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        classItem.is_active === false
                          ? 'bg-red-100 text-red-600'
                          : 'bg-orange-100 text-[#E67E22]'
                      }`}
                    >
                      {count}/{max}
                    </span>
                  </div>

                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getEffectifColor(count, max)}`}
                        style={{ width: `${ratio}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {count === 0
                        ? 'Aucun élève'
                        : `${count} élève${count > 1 ? 's' : ''} inscrit${count > 1 ? 's' : ''}`}
                    </p>
                  </div>

                  {/* 👨‍🏫 PROF PRINCIPAL */}
                  <div className="flex items-center space-x-2 mb-4 text-sm">
                    <span className="text-gray-400">👨‍🏫</span>
                    {hasNoTeacher ? (
                      <span className="text-red-500 italic font-medium">
                        ⚠️ Aucun prof principal
                      </span>
                    ) : (
                      <span className="text-gray-700 font-medium truncate">
                        {teacherName}
                      </span>
                    )}
                  </div>

                  <div className="flex space-x-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/admin/classes/${classItem.id}`)}
                      className="flex-1 text-sm bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 transition font-medium"
                    >
                      👁️ Voir
                    </button>
                    <button
                      onClick={() => handleEdit(classItem)}
                      className="text-sm bg-gray-50 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(classItem.id)}
                      className="text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========== MODAL ========== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">
              {editingClass ? '✏️ Modifier la classe' : '➕ Créer une classe'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Année scolaire */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Année scolaire *
                </label>
                <select
                  value={formData.academic_year}
                  onChange={(e) =>
                    setFormData({ ...formData, academic_year: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  {ACADEMIC_YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Niveau */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Niveau *
                </label>
                <select
                  value={formData.level}
                  onChange={(e) =>
                    setFormData({ ...formData, level: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  <option value="">-- Sélectionner un niveau --</option>
                  {LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {lvl}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nom de la classe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la classe (division) *
                </label>
                <input
                  type="text"
                  placeholder="Ex: A, B, C, D..."
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                  maxLength={10}
                />
                {formData.level && formData.name && (
                  <p className="text-xs text-blue-600 mt-1">
                    → La classe s'appellera : <strong>{formData.level} {formData.name}</strong>
                  </p>
                )}
              </div>

              {/* Salle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salle
                </label>
                <input
                  type="text"
                  placeholder="Ex: Salle 12"
                  value={formData.room}
                  onChange={(e) =>
                    setFormData({ ...formData, room: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                />
              </div>

              {/* Capacité max */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacité maximale
                </label>
                <input
                  type="number"
                  placeholder="40"
                  value={formData.max_students}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_students: parseInt(e.target.value) || 40,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  min="1"
                  max="200"
                />
              </div>

              {/* 👑 Professeur principal — avec spécialité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  👑 Professeur principal
                </label>
                <select
                  value={formData.class_teacher_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      class_teacher_id: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                >
                  <option value="">-- Aucun --</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.speciality ? ` — ${t.speciality}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Vous pouvez changer le prof principal à tout moment.
                </p>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === true}
                      onChange={() =>
                        setFormData({ ...formData, is_active: true })
                      }
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">● Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === false}
                      onChange={() =>
                        setFormData({ ...formData, is_active: false })
                      }
                      className="accent-[#E67E22]"
                    />
                    <span className="text-sm">○ Inactive</span>
                  </label>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingClass(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition font-medium"
                >
                  {editingClass ? '💾 Modifier' : '✅ Créer la classe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClasses;